import {Component, computed, effect, inject, output, signal} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {MatButton, MatIconButton} from '@angular/material/button';
import {MatIcon} from '@angular/material/icon';
import {TranslateModule} from '@ngx-translate/core';
import {HttpClient, HttpContext} from '@angular/common/http';
import {SKIP_EXCEPTION_HANDLING} from '../../tokens/skip-exception-handling.token';
import {CommentContextService} from '../../services/comment-context.service';
import {SessionService} from '../../services/session.service';
import {CommentCollectionHal, CommentHal} from '../../models/HAL/comment-hal.model';
import {DatePipe} from '@angular/common';
import {MatMenuModule} from '@angular/material/menu';
import {MatTooltipModule} from '@angular/material/tooltip';
import {DialogService} from '../../services/dialog.service';
import {SnackBarService} from '../../services/snackbar.service';
import {DynamicConfirmationDialogComponent} from '../dynamic-confirmation-dialog/dynamic-confirmation-dialog.component';

@Component({
  selector: 'app-comments',
  imports: [FormsModule, MatIconButton, MatButton, MatIcon, TranslateModule, DatePipe, MatMenuModule, MatTooltipModule],
  templateUrl: './comments.component.html',
  styleUrl: './comments.component.scss',
})
export class CommentsComponent {
  closed = output<void>();

  commentText = signal('');
  isInputExpanded = signal(false);
  comments = signal<CommentHal[]>([]);
  loading = signal(false);
  loadError = signal(false);
  saving = signal(false);
  editingCommentId = signal<string | null>(null);
  editText = signal('');
  savingEdit = signal(false);
  expandedComments = new Set<string>();
  readonly maxLength = 500;

  private readonly httpClient = inject(HttpClient);
  private readonly commentContextService = inject(CommentContextService);
  private readonly sessionService = inject(SessionService);
  private readonly dialogService = inject(DialogService);
  private readonly snackBarService = inject(SnackBarService);

  protected readonly getCommentsLink = computed(() => this.commentContextService.panelHal()?._links.getLink('getComments')?.href);
  protected readonly createCommentLink = computed(() => this.commentContextService.panelHal()?._links.getLink('createComment')?.href);
  protected readonly caseId = computed(() => this.commentContextService.panelHal()?.identification);

  constructor() {
    effect(() => {
      const link = this.getCommentsLink();
      if (link) {
        this.loadComments();
      } else {
        this.comments.set([]);
        this.loadError.set(false);
      }
    });
  }

  onFocus() {
    this.isInputExpanded.set(true);
  }

  onBlur() {
    if (this.commentText().trim().length === 0) {
      this.isInputExpanded.set(false);
    }
  }

  onCancel() {
    this.commentText.set('');
    this.isInputExpanded.set(false);
  }

  onSave() {
    const content = this.commentText().trim();
    if (content.length === 0) {
      return;
    }
    const createLink = this.createCommentLink();
    const caseId = this.caseId();
    if (!createLink || !caseId) {
      return;
    }
    this.saving.set(true);
    const authorId = this.sessionService.getSession().user?.identification;
    const request = {
      comment: {content, authorId},
      references: [{caseId, status: 'UNREAD'}]
    };
    this.httpClient.post(createLink, request).subscribe({
      next: () => {
        this.commentText.set('');
        this.isInputExpanded.set(false);
        this.saving.set(false);
        this.loadComments();
      },
      error: () => {
        this.saving.set(false);
      }
    });
  }

  onEdit(comment: CommentHal) {
    this.editingCommentId.set(comment.identification);
    this.editText.set(comment.content);
  }

  onCancelEdit() {
    this.editingCommentId.set(null);
    this.editText.set('');
  }

  onSaveEdit(comment: CommentHal) {
    const content = this.editText().trim();
    if (content.length === 0) {
      return;
    }
    const updateLink = comment._links.getLink('update')?.href;
    if (!updateLink) {
      return;
    }
    this.savingEdit.set(true);
    const request = {
      content
    };
    this.httpClient.put(updateLink, request).subscribe({
      next: () => {
        this.editingCommentId.set(null);
        this.editText.set('');
        this.savingEdit.set(false);
        this.loadComments();
      },
      error: () => {
        this.savingEdit.set(false);
      }
    });
  }

  onDelete(comment: CommentHal) {
    const deleteLink = comment._links.getLink('delete')?.href;
    if (!deleteLink) {
      return;
    }
    this.dialogService.open(DynamicConfirmationDialogComponent, {
      data: {
        title: 'comments.deleteDialog.title',
        message: 'comments.deleteDialog.message',
        confirmButtonText: 'comments.deleteDialog.confirm',
        cancelButtonText: 'comments.deleteDialog.cancel'
      }
    }).afterClosed().subscribe(result => {
      if (result) {
        this.httpClient.delete(deleteLink, {responseType: 'text'}).subscribe({
          next: () => {
            this.snackBarService.open('comments.deleteSuccess', 3);
            this.loadComments();
          }
        });
      }
    });
  }

  toggleExpand(commentId: string) {
    if (this.expandedComments.has(commentId)) {
      this.expandedComments.delete(commentId);
    } else {
      this.expandedComments.add(commentId);
    }
  }

  close() {
    this.closed.emit();
  }

  retryLoad() {
    this.loadComments();
  }

  private loadComments() {
    const link = this.getCommentsLink();
    if (!link) {
      return;
    }
    this.loading.set(true);
    this.loadError.set(false);
    // The comments are loaded as soon as a case is opened, so a failure must not raise the global exception dialog.
    // It is rendered inside the panel instead, where it is relevant to the user.
    const context = new HttpContext().set(SKIP_EXCEPTION_HANDLING, true);
    this.httpClient.get<any>(link, {context}).subscribe({
      next: (response) => {
        const collection = new CommentCollectionHal(response);
        const comments = collection._embedded?.comments ?? [];
        comments.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
        this.comments.set(comments);
        this.loading.set(false);
      },
      error: () => {
        this.comments.set([]);
        this.loadError.set(true);
        this.loading.set(false);
      }
    });
  }
}
